import React, {useEffect, useMemo, useState} from 'react';
import axios from "axios";
import {MagnifyingGlass, Users, UserPlus, Plus, ArrowRight} from '@phosphor-icons/react';
import {getBaseUrlNodeServer} from "../../config";

const AddLeads = ({currentCampaign, setParentSelectedContacts, parentSelectedContacts}) => {
	const verifiedemail = sessionStorage.getItem("verifiedemail");
	const [usercontacts, setusercontacts] = useState([]);
	const [selectedIds, setSelectedIds] = useState(() => {
		const init = {};
		(parentSelectedContacts || []).forEach(c => {
			if (c && c._id) init[c._id] = true;
		});
		return init;
	});
	const [isLoading, setIsLoading] = useState(true);
	const [query, setQuery] = useState('');

	useEffect(() => {
		let cancelled = false;
		async function fetchContacts() {
			try {
				axios.defaults.baseURL = getBaseUrlNodeServer();
				const response = await axios.post('getusercontacts', {verifiedemail});
				if (!cancelled) {
					setusercontacts(response.data || []);
					setIsLoading(false);
				}
			} catch (error) {
				console.error('Error during fetch:', error.message || error);
				if (!cancelled) setIsLoading(false);
			}
		}
		fetchContacts();
		return () => { cancelled = true; };
	}, [verifiedemail]);

	// Sync hydrate when parent selection changes (and after contacts arrive).
	useEffect(() => {
		if (!parentSelectedContacts) return;
		const next = {};
		parentSelectedContacts.forEach(c => {
			if (c && c._id) next[c._id] = true;
		});
		setSelectedIds(next);
	}, [parentSelectedContacts, usercontacts.length]);

	const filteredContacts = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return usercontacts;
		return usercontacts.filter(c => {
			const hay = [
				c.Email, c['First Name'], c['Last Name'], c['Position']
			].filter(Boolean).join(' ').toLowerCase();
			return hay.includes(q);
		});
	}, [usercontacts, query]);

	const selectedCount = useMemo(
		() => Object.values(selectedIds).filter(Boolean).length,
		[selectedIds]
	);

	const propagate = (nextSelected) => {
		const chosen = usercontacts.filter(c => c && c._id && nextSelected[c._id]);
		setParentSelectedContacts(chosen);
	};

	const toggleContact = (contact) => {
		if (!contact || !contact._id) return;
		const next = {...selectedIds, [contact._id]: !selectedIds[contact._id]};
		setSelectedIds(next);
		propagate(next);
	};

	const selectAllVisible = () => {
		const next = {...selectedIds};
		filteredContacts.forEach(c => { if (c && c._id) next[c._id] = true; });
		setSelectedIds(next);
		propagate(next);
	};

	const clearAll = () => {
		setSelectedIds({});
		setParentSelectedContacts([]);
	};

	const initials = (c) => {
		const f = (c['First Name'] || '').trim();
		const l = (c['Last Name'] || '').trim();
		if (f || l) return `${f[0] || ''}${l[0] || ''}`.toUpperCase();
		return (c.Email || '?')[0].toUpperCase();
	};

	return (
		<div className="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col"
		     style={{maxHeight: '80vh'}}>
			{/* Header */}
			<div className="px-6 pt-6 pb-4 border-b border-slate-100">
				<div className="flex items-start justify-between gap-4">
					<div>
						<h2 className="text-2xl font-semibold text-slate-900 tracking-tight">Add leads</h2>
						<p className="text-sm text-slate-600 mt-1">
							Campaign:&nbsp;
							<span className="font-medium text-slate-900">
								{currentCampaign?.campaignname || 'Untitled'}
							</span>
						</p>
					</div>
					<div className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-sm text-emerald-700 font-medium whitespace-nowrap">
						{selectedCount} selected
					</div>
				</div>

				{/* Search */}
				<div className="mt-4 relative">
					<MagnifyingGlass
						size={16}
						className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
					<input
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Search by name, email, or position"
						className="w-full border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition-colors"/>
				</div>

				{/* Quick actions */}
				{!isLoading && usercontacts.length > 0 && (
					<div className="mt-3 flex items-center gap-3 text-xs">
						<button
							type="button"
							onClick={selectAllVisible}
							className="text-emerald-700 hover:text-emerald-800 font-medium">
							Select all{query ? ' matching' : ''}
						</button>
						<span className="text-slate-300">·</span>
						<button
							type="button"
							onClick={clearAll}
							className="text-slate-600 hover:text-slate-900 font-medium">
							Clear selection
						</button>
					</div>
				)}
			</div>

			{/* Body */}
			<div className="flex-1 overflow-y-auto px-3 py-2">
				{isLoading && (
					<div className="px-4 py-12 text-center text-sm text-slate-500">
						Loading your contacts&hellip;
					</div>
				)}

				{!isLoading && usercontacts.length === 0 && (
					<div className="px-6 py-12 text-center">
						<div className="w-12 h-12 mx-auto rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
							<Users size={22} weight="duotone" color="#10B981"/>
						</div>
						<h3 className="text-base font-semibold text-slate-900">No contacts yet</h3>
						<p className="mt-1 text-sm text-slate-600">
							Import your first contacts to start adding leads to campaigns.
						</p>
						<a
							href="/contacts"
							className="inline-flex items-center gap-2 mt-5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors">
							<UserPlus size={16} weight="bold"/>
							Import your first contacts
							<ArrowRight size={14} weight="bold"/>
						</a>
					</div>
				)}

				{!isLoading && usercontacts.length > 0 && filteredContacts.length === 0 && (
					<div className="px-4 py-12 text-center text-sm text-slate-500">
						No contacts match &ldquo;{query}&rdquo;.
					</div>
				)}

				{!isLoading && filteredContacts.length > 0 && (
					<ul className="flex flex-col gap-1">
						{filteredContacts.map((contact) => {
							const isOn = !!selectedIds[contact._id];
							return (
								<li
									key={contact._id || contact.Email}
									onClick={() => toggleContact(contact)}
									className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors ${
										isOn ? 'bg-emerald-50/70 border border-emerald-200' : 'border border-transparent hover:bg-emerald-50/40'
									}`}>
									<input
										type="checkbox"
										checked={isOn}
										onChange={() => toggleContact(contact)}
										onClick={(e) => e.stopPropagation()}
										className="accent-emerald-500 w-4 h-4 shrink-0 cursor-pointer"/>
									<div className="w-9 h-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-xs font-semibold text-emerald-700 shrink-0">
										{initials(contact)}
									</div>
									<div className="flex-1 min-w-0 text-left">
										<div className="text-sm font-medium text-slate-900 truncate">
											{[contact['First Name'], contact['Last Name']].filter(Boolean).join(' ') || contact.Email}
										</div>
										<div className="text-xs text-slate-500 truncate">
											{contact.Email}
											{contact['Position'] ? ` · ${contact['Position']}` : ''}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</div>

			{/* Footer / CTA */}
			<div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 rounded-b-2xl flex items-center justify-between gap-3">
				<span className="text-sm text-slate-600">
					{selectedCount > 0
						? <><span className="font-semibold text-slate-900">{selectedCount}</span> contact{selectedCount === 1 ? '' : 's'} ready to add</>
						: <span>Pick contacts to attach to this campaign</span>}
				</span>
				<button
					type="button"
					disabled={selectedCount === 0}
					onClick={() => propagate(selectedIds)}
					className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
						selectedCount === 0
							? 'bg-slate-200 text-slate-400 cursor-not-allowed'
							: 'bg-emerald-500 hover:bg-emerald-600 text-white'
					}`}>
					<Plus size={14} weight="bold"/>
					Add to campaign
				</button>
			</div>
		</div>
	);
};

export default AddLeads;
